import { useState } from "react";
import { 
  CheckCircleIcon, 
  Cog6ToothIcon, 
  ExclamationCircleIcon, 
  XCircleIcon, 
  PlusIcon, 
  ArrowPathIcon 
} from "@heroicons/react/24/outline";
import { BookmarkIcon } from "@heroicons/react/24/solid";

export default function SubstepCreator({ taskData, processId, setOpenSubstepCreator, handleGetStep, setOpenSubstepViewer }) {
  const [substeps, setSubsteps] = useState([]);
  const [form, setForm] = useState({
    name: "",
    step: "",
    description: "",
    type: "Action",
  });
  const [isLoading, setIsLoading] = useState(false);

  console.log("Task Data:", taskData);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.step.trim() || !form.description.trim()) return;

    setSubsteps([...substeps, { ...form, id: Date.now() }]);
    setForm({ name: "", step: "", description: "", type: "Action" });
  };

  const removeSubstep = (id) => {
    setSubsteps(substeps.filter(substep => substep.id !== id));
  };

  const typeConfig = {
    Action: {
      icon: <Cog6ToothIcon className="h-4 w-4" />,
      color: "bg-blue-500",
      lightColor: "bg-blue-50",
      textColor: "text-blue-700",
      borderColor: "border-blue-200"
    },
    Bridge: {
      icon: <ExclamationCircleIcon className="h-4 w-4" />,
      color: "bg-amber-500",
      lightColor: "bg-amber-50",
      textColor: "text-amber-700",
      borderColor: "border-amber-200"
    },
    Expectation: {
      icon: <CheckCircleIcon className="h-4 w-4" />,
      color: "bg-purple-500",
      lightColor: "bg-purple-50",
      textColor: "text-purple-700",
      borderColor: "border-purple-200"
    }
  };

  const handleSaveAll = async () => {
    if (substeps.length === 0) return;
    
    setIsLoading(true);
    console.log("Saving substeps:", substeps);
    console.log("Task ID:", taskData.id);

    try {
      const requests = substeps.map(async (substep) => {
        const response = await fetch('https://api.insightos.co.uk/api/savesubstep', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            TaskID: taskData.id,
            Name: substep.name,
            Step: substep.step,
            Description: substep.description,
            Type: substep.type,
            ProcessId: processId,
          }),
        });

        return response.json();
      });

      const results = await Promise.all(requests);
      const failedRequests = results.filter((res) => !res.success);

     if (failedRequests.length > 0) {
    console.error("Some substeps failed to save:", failedRequests);
} else {
    console.log("All substeps saved successfully.");
    setSubsteps([]);

    if (typeof handleGetStep === "function") {
        await handleGetStep(processId, taskData.ProcessName);
    }

    // Refresh the substep viewer with the same task so Viewsubsteps re-renders
    if (typeof setOpenSubstepViewer === "function") {
        setOpenSubstepViewer({ ...taskData });  // ← new object reference triggers re-render
    }

    if (typeof setOpenSubstepCreator === "function") {
        setOpenSubstepCreator(null);
    }
}
      
    } catch (error) {
      console.error("Error saving substeps:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl font-semibold text-slate-900">Create Substeps</h1>
              {taskData?.name && (
                <span className="hidden sm:inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                  {taskData.name}
                </span>
              )}
            </div>
            <button
              onClick={() => setOpenSubstepCreator(null)}
              data-component="substep-creator"
              data-feature="close"
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors"
            >
              <XCircleIcon className="h-4 w-4 mr-2" />
              Close
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Form Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="mb-6">
                <h2 className="text-lg font-medium text-slate-900 mb-2">Add New Substep</h2>
                <p className="text-sm text-slate-600">Fill in the details below to create a new substep for this task.</p>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Substep Name
                    </label>
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      type="text"
                      required
                      placeholder="Enter substep name"
                      className="block w-full px-3 py-2.5 border border-slate-300 rounded-lg shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Step Number
                    </label>
                    <input
                      name="step"
                      value={form.step}
                      onChange={handleChange}
                      type="number"
                      required
                      placeholder="e.g., 1"
                      min="1"
                      className="block w-full px-3 py-2.5 border border-slate-300 rounded-lg shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Type
                  </label>
                  <select
                    name="type"
                    value={form.type}
                    onChange={handleChange}
                    required
                    className="block w-full px-3 py-2.5 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors"
                  >
                    <option value="Action">Action</option>
                    <option value="Bridge">Bridge</option>
                    <option value="Expectation">Expectation</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    rows="4"
                    required
                    placeholder="Describe what this substep involves..."
                    className="block w-full px-3 py-2.5 border border-slate-300 rounded-lg shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm resize-none transition-colors"
                  />
                </div>

                <button
                  onClick={handleNext}
                  data-component="substep-creator"
                  data-feature="add-substep"
                  className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Add Substep
                </button>
              </div>
            </div>
          </div>

          {/* Substeps Preview */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-medium text-slate-900">Substeps Preview</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    {substeps.length} substep{substeps.length !== 1 ? 's' : ''} added
                  </p>
                </div>
                {substeps.length > 0 && (
                  <button
                    onClick={handleSaveAll}
                    disabled={isLoading}
                    data-component="substep-creator"
                    data-feature="save-all"
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoading ? (
                      <>
                        <ArrowPathIcon className="animate-spin h-4 w-4 mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <BookmarkIcon className="h-4 w-4 mr-2" />
                        Save All
                      </>
                    )}
                  </button>
                )}
              </div>

              {substeps.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto bg-slate-100 rounded-full flex items-center justify-center mb-4">
                    <PlusIcon className="h-8 w-8 text-slate-400" />
                  </div>
                  <p className="text-sm text-slate-500">No substeps added yet</p>
                  <p className="text-xs text-slate-400 mt-1">Add your first substep using the form</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {substeps
                    .sort((a, b) => parseInt(a.step) - parseInt(b.step))
                    .map((substep, idx) => {
                      const config = typeConfig[substep.type];
                      return (
                        <div
                          key={substep.id}
                          className={`relative p-4 rounded-lg border ${config.borderColor} ${config.lightColor} transition-all hover:shadow-sm group`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3 flex-1 min-w-0">
                              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${config.color} text-white flex-shrink-0`}>
                                {config.icon}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <p className="text-sm font-medium text-slate-900 truncate">
                                    {substep.name}
                                  </p>
                                  <span className="text-xs text-slate-500 ml-2 flex-shrink-0">
                                    Step {substep.step}
                                  </span>
                                </div>
                                <p className="text-xs text-slate-600 line-clamp-2">
                                  {substep.description}
                                </p>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.textColor} ${config.lightColor} mt-2`}>
                                  {substep.type}
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={() => removeSubstep(substep.id)}
                              data-component="substep-creator"
                              data-feature="remove-substep"
                              className="ml-2 p-1 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                            >
                              <XCircleIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}